import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { INotesSelectComponent } from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue, INameId, ViewMode } from '@modeso/types__aumico-lib-core-be';
import { ISelectOptions } from '@modeso/types__aumico-lib-notes-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { SelectComponentHelper } from '../../helpers/selectComponent.helper';
import { IAnnualReportDefaultSettings } from '../../interfaces/IAnnualReportDefaultSettings.interface';
import { NotesPageBase } from '../NotesPageBase';
import { ISelectDelegate } from './ISelectDelegate.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:SelectComponent');

@UntilDestroy()
@Component({
    selector: 'aumico-notes-select',
    templateUrl: 'select.component.html',
    styleUrls: ['select.component.scss'],
})
export class SelectComponent extends NotesPageBase implements OnChanges {
    @Input() isExtraSettingsOn: boolean;

    @Input() language: string;

    @Input() componentData: INotesSelectComponent = {
        previousYearDate: '2020-12-31T22:00:00.000Z',
        currentYearDate: '2021-12-31T22:00:00.000Z',
        currentYearValue: '',
        previousYearValue: '',
        options: { text: {}, selectionOption: [], title: {} },
        footnotes: [],
    };
    @Input() componentOptions: ISelectOptions = this.componentData.options;

    @Input() delegate: ISelectDelegate;

    @Input() viewMode: ViewMode = ViewMode.EDIT;
    @Input() supportedLocales: string[];
    @Input() componentIndex: number;

    @Input() highlightTodos = false;
    @Input() tableNumber: string | undefined;


    @Input() set annualReportDefaultSettings(value: IAnnualReportDefaultSettings) {
        this.componentData = {
            ...this.componentData,
            currentYearDate: value.currentBalanceDate,
            previousYearDate: value.previousBalanceDate,
        }


    }


    @Output() onStatusChange = new EventEmitter<boolean>();
    @Output() onSettingsStatusChange = new EventEmitter<boolean>();
    form: UntypedFormGroup;
    text: string;
    selectionOptions: INameId[] = [];
    selectData: INotesSelectComponent;
    showOptions = false;
    footnotes: string[] = [];

    constructor(private fb: UntypedFormBuilder, private selectComponentHelper: SelectComponentHelper) {
        super();
    }

    public toggleSettings() {
        this.showOptions = !this.showOptions;
    }
    public onCloseOptions(value: boolean) {
        this.showOptions = value;
        this.onSettingsStatusChange.emit(value);
    }

    ngOnChanges(change: SimpleChanges) {
        debug(change);
        if (change['componentData'] && change['componentData'].currentValue) {
            this.componentOptions = change['componentData'].currentValue.options;

            if (!change['componentData'].previousValue) {
                this.initializeForm();
                this.setComponentOptionsLanguage();

                this.setFormValues();
                this.formValueChanges();
            }

            if (this.componentOptions['settings']) {
                this.showOptions = this.componentOptions['settings'];
            }
        }

        if (change['language'] && change['language'].currentValue) {
            this.setComponentOptionsLanguage();
            if (this.form) {
                this.setFormValues();
            }
        }
    }

    initializeForm() {
        this.form = this.fb.group(this.createSelectFields());
    }

    createSelectFields() {
        const formGroup = {};
        debug('component data', this.componentData);
        formGroup['title'] = new UntypedFormControl();
        formGroup['text'] = new UntypedFormControl();
        formGroup['footnote'] = new UntypedFormControl();
        formGroup['selectionForCurrentYearDate'] = new UntypedFormControl(
            { value: 'undefined', disabled: false },
            this.viewMode != ViewMode.PREVIEW ? [] : [],
        );
        formGroup['selectionForPreviousYearDate'] = new UntypedFormControl(
            { value: 'undefined', disabled: false },
            this.viewMode != ViewMode.PREVIEW ? [] : [],
        );

        return formGroup;
    }

    formValueChanges() {
        this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((formValue) => {
            debug('form change', formValue);

            if (formValue.text != null) {
                this.text = formValue.text;
                this.componentOptions.text[this.language] = formValue.text;
            }

            if (formValue.footnote != null) {
                this.footnotes = [formValue.footnote];
            }

            this.emitComponentData();
        });
    }

    setComponentOptionsLanguage() {
        if (this.componentOptions.text) {
            this.text = this.componentOptions.text?.[this.language];
        }

        if (this.componentOptions.selectionOption) {
            this.componentOptions.selectionOption.forEach((option: IDynamicKeyValue, index: number) => {
                const opt = {
                    id: option[this.language],
                    name: option[this.language],
                };
                this.selectionOptions.push(opt);
            });
        }
    }

    setFormValues() {
        const selectionForCurrentYearDate: INameId = this.selectComponentHelper.getSelectedOptionOfSelectionOptions(
            this.selectionOptions,
            this.componentData.currentYearValue,
        );

        const selectionForPreviousYearDate: INameId = this.selectComponentHelper.getSelectedOptionOfSelectionOptions(
            this.selectionOptions,
            this.componentData.previousYearValue,
        );

        if (this.form.controls) {
            this.form.controls['selectionForCurrentYearDate'].setValue(selectionForCurrentYearDate);
            this.form.controls['selectionForPreviousYearDate'].setValue(selectionForPreviousYearDate);
            this.form.controls['title'].setValue(this.componentData.options.title?.[this.language]);
            this.form.controls['text'].setValue(this.componentData.options.text?.[this.language]);
            this.form.controls['footnote'].setValue(this.componentData.footnotes?.[0] || '');
        }
    }

    onOptionsChanged(options: ISelectOptions): void {
        const { text, selectionOption } = options;
        this.componentOptions.text = text;
        this.componentOptions.selectionOption = selectionOption;

        this.selectionOptions = options.selectionOption.map((option) => ({
            id: option[this.language],
            name: option[this.language],
        }));

        this.text = options.text?.[this.language];

        this.emitComponentData();
    }

    onFormStatusChange(isValid: boolean): void {
        this.onStatusChange.emit(isValid);
    }

    onTitleChanged(updatedTitle: string): void {
        if (!this.componentOptions.title) {
            this.componentOptions.title = {} as Record<string, string>;
        }

        this.componentOptions.title[this.language] = updatedTitle;

        this.emitComponentData();
    }

    private emitComponentData(): void {
        this.updateSelectData();

        if (this.delegate != null) {
            this.delegate.selectComponentData(this.selectData, this.componentIndex);
        }
    }

    private updateSelectData(): void {
        this.selectData = {
            currentYearDate: this.componentData.currentYearDate,
            previousYearDate: this.componentData.previousYearDate,
            previousYearValue: this.form.getRawValue().selectionForPreviousYearDate
                ? this.form.getRawValue().selectionForPreviousYearDate.id
                : undefined,
            currentYearValue: this.form.getRawValue().selectionForCurrentYearDate
                ? this.form.getRawValue().selectionForCurrentYearDate.id
                : undefined,

            options: this.componentOptions,
            addNumbering: this.addNumbering,
            referenceFlis: this.referenceFlis,
            footnotes: this.footnotes,
        };
    }
}
